/**
 * @description Utility functions for gradient CSS generation and parsing
 * @author Form Survey Builder
 */

export interface ColorStop {
  id: number;
  color: string;
  stop: string;
}

export interface GradientConfig {
  enabled: boolean;
  type: "linear" | "radial" | "conic";
  angle: number;
  colors: ColorStop[];
}

/**
 * Converts a hex color string to CSS format
 * @param hexColor - Hex color string (with or without #)
 * @returns CSS color string
 */
export const formatColorForCSS = (hexColor: string): string => {
  if (!hexColor) {
    return "#000000";
  }

  // Remove # if present
  const cleanHex = hexColor.replace("#", "");

  // Add # prefix
  return `#${cleanHex}`;
};

/**
 * Converts a CSS color string to hex format (without #)
 * @param cssColor - CSS color string
 * @returns Hex color string without #
 */
export const formatColorFromCSS = (cssColor: string): string => {
  if (!cssColor) {
    return "000000";
  }

  // Remove # if present
  return cssColor.replace("#", "");
};

/**
 * Generates CSS gradient string from gradient configuration
 * @param config - Gradient configuration
 * @returns CSS gradient string
 */
export const gradientToCss = (config: GradientConfig): string => {
  if (!config.enabled || !config.colors || config.colors.length === 0) {
    return "none";
  }

  // Sort colors by stop position to ensure proper gradient order
  const sortedColors = [...config.colors].sort((a, b) => {
    const aStop = parseInt(a.stop.replace("%", ""));
    const bStop = parseInt(b.stop.replace("%", ""));
    return aStop - bStop;
  });

  const colorsString = sortedColors
    .map((color) => `${formatColorForCSS(color.color)} ${color.stop}`)
    .join(", ");

  switch (config.type) {
    case "linear":
      return `linear-gradient(${config.angle}deg, ${colorsString})`;
    case "radial":
      return `radial-gradient(circle, ${colorsString})`;
    case "conic":
      return `conic-gradient(from ${config.angle}deg, ${colorsString})`;
    default:
      return `linear-gradient(${config.angle}deg, ${colorsString})`;
  }
};

/**
 * Parses CSS gradient string into gradient configuration
 * @param cssString - CSS gradient string
 * @returns Parsed gradient configuration
 */
export const parseGradientFromCss = (cssString: string): GradientConfig => {
  const result: GradientConfig = {
    enabled: false,
    type: "linear",
    angle: 90,
    colors: [
      { id: 1, color: "#FFFFFF", stop: "0%" },
      { id: 10, color: "#188BF6", stop: "100%" },
    ],
  };

  if (!cssString || cssString === "none" || !cssString.includes("gradient")) {
    return result;
  }

  const cleanString = cssString.replace(/\s+/g, " ").trim();
  result.enabled = true;

  if (cleanString.includes("linear-gradient(")) {
    result.type = "linear";
    const content = cleanString.replace("linear-gradient(", "").replace(/\)$/, "");
    const parts = content.split(",");

    if (parts.length > 1) {
      result.angle = parseInt(parts[0].trim()) || 90;
      const colorParts = parts.slice(1);
      result.colors = parseColorStops(colorParts);
    }
  } else if (cleanString.includes("radial-gradient(")) {
    result.type = "radial";
    const content = cleanString.replace("radial-gradient(", "").replace(/\)$/, "");
    const parts = content.split(",");

    if (parts.length > 0) {
      const firstPart = parts[0].trim();
      if (firstPart === "circle" || firstPart === "ellipse") {
        const colorParts = parts.slice(1);
        result.colors = parseColorStops(colorParts);
      } else {
        result.colors = parseColorStops(parts);
      }
    }
  } else if (cleanString.includes("conic-gradient(")) {
    result.type = "conic";
    const content = cleanString.replace("conic-gradient(", "").replace(/\)$/, "");
    const parts = content.split(",");

    if (parts.length > 1) {
      const fromPart = parts[0].trim();
      if (fromPart.startsWith("from ")) {
        result.angle = parseInt(fromPart.replace("from ", "").trim()) || 0;
        const colorParts = parts.slice(1);
        result.colors = parseColorStops(colorParts);
      }
    }
  }

  // Ensure we have proper color IDs
  result.colors = result.colors.map((color, index) => {
    let id: number;
    if (index === 0) {
      id = 1; // Start color
    } else if (index === result.colors.length - 1) {
      id = 10; // End color
    } else {
      id = index + 1; // Middle colors
    }
    return { ...color, id };
  });

  return result;
};

/**
 * Parses color stops from CSS gradient parts
 * @param colorParts - Array of color stop strings
 * @returns Array of color stop objects
 */
const parseColorStops = (colorParts: string[]): ColorStop[] => {
  return colorParts.map((part, index) => {
    const trimmed = part.trim();
    const colorStopSplit = trimmed.split(" ");

    let id: number;
    if (index === 0) {
      id = 1; // Start color
    } else if (index === colorParts.length - 1) {
      id = 10; // End color
    } else {
      id = index + 1; // Middle colors
    }

    if (colorStopSplit.length >= 2) {
      return {
        id,
        color: colorStopSplit[0],
        stop: colorStopSplit[1].replace(/\)+$/, ""),
      };
    }
    return {
      id,
      color: trimmed.replace(/\)+$/, ""),
      stop: `${index * (100 / (colorParts.length - 1))}%`,
    };
  });
};

/**
 * Creates default gradient configuration from a solid color
 * @param solidColor - Solid color hex string
 * @returns Default gradient configuration
 */
export const createDefaultGradientFromColor = (solidColor: string): GradientConfig => {
  return {
    enabled: false,
    type: "linear",
    angle: 90,
    colors: [
      { id: 1, color: solidColor, stop: "0%" },
      { id: 10, color: solidColor, stop: "100%" },
    ],
  };
};

/**
 * Validates gradient configuration
 * @param config - Gradient configuration to validate
 * @returns Validated gradient configuration
 */
export const validateGradientConfig = (config: GradientConfig): GradientConfig => {
  const validated = { ...config };

  // Clamp angle between 0 and 360
  validated.angle = Math.max(0, Math.min(360, validated.angle));

  // Ensure we have at least 2 colors
  if (validated.colors.length < 2) {
    validated.colors = [
      { id: 1, color: "#FFFFFF", stop: "0%" },
      { id: 10, color: "#188BF6", stop: "100%" },
    ];
  }

  // Validate color stops
  validated.colors = validated.colors.map((color) => {
    const stopPercentage = parseInt(color.stop.replace("%", ""));
    const clampedStop = Math.max(0, Math.min(100, stopPercentage));
    return {
      ...color,
      stop: `${clampedStop}%`,
    };
  });

  return validated;
};

/**
 * Adds a new color stop to the gradient
 * @param colors - Current color stops
 * @param percentage - Position percentage for new color
 * @returns Updated color stops array
 */
export const addColorStop = (colors: ColorStop[], percentage?: number): ColorStop[] => {
  if (colors.length >= 10) {
    return colors;
  }

  let newStopPercentage: number;

  if (percentage !== undefined) {
    newStopPercentage = Math.max(0, Math.min(100, percentage));
  } else {
    const middleColors = colors.filter((color) => color.stop !== "0%" && color.stop !== "100%");

    if (middleColors.length > 0) {
      const lastMiddleStop = Math.max(
        ...middleColors.map((color) => parseInt(color.stop.replace("%", "")))
      );
      newStopPercentage = Math.min(lastMiddleStop + 10, 90);
    } else {
      newStopPercentage = 50;
    }
  }

  // Find available ID for middle colors (2-9)
  const usedIds = colors.map((c) => c.id);
  const availableIds = [2, 3, 4, 5, 6, 7, 8, 9].filter((id) => !usedIds.includes(id));
  const newId = availableIds[0] || 2;

  const newColor: ColorStop = {
    id: newId,
    color: "#9AE6B4",
    stop: `${newStopPercentage}%`,
  };

  // Insert at correct position
  const insertIndex = colors.findIndex((color) => {
    const colorStop = parseInt(color.stop.replace("%", ""));
    return colorStop > newStopPercentage;
  });

  const updatedColors = [...colors];
  if (insertIndex === -1) {
    updatedColors.splice(-1, 0, newColor);
  } else {
    const actualInsertIndex = Math.max(1, insertIndex);
    updatedColors.splice(actualInsertIndex, 0, newColor);
  }

  return updatedColors;
};

/**
 * Removes a color stop from the gradient
 * @param colors - Current color stops
 * @param id - ID of color stop to remove
 * @returns Updated color stops array
 */
export const removeColorStop = (colors: ColorStop[], id: number): ColorStop[] => {
  // Don't remove start (id: 1) or end (id: 10) colors
  if (id === 1 || id === 10) {
    return colors;
  }

  return colors.filter((color) => color.id !== id);
};

/**
 * Updates a color stop in the gradient
 * @param colors - Current color stops
 * @param id - ID of color stop to update
 * @param field - Field to update ('color' or 'stop')
 * @param value - New value
 * @returns Updated color stops array
 */
export const updateColorStop = (
  colors: ColorStop[],
  id: number,
  field: "color" | "stop",
  value: string
): ColorStop[] => {
  return colors.map((color) => {
    if (color.id === id) {
      if (field === "color") {
        return { ...color, color: value };
      } else if (field === "stop") {
        const percentage = parseInt(value.replace("%", ""));
        if (isNaN(percentage) || percentage < 0 || percentage > 100) {
          return color; // Don't update invalid values
        }
        return {
          ...color,
          stop: value.includes("%") ? value : `${value}%`,
        };
      }
    }
    return color;
  });
};
